"use client";

import { useMemo, useRef, useState } from "react";
import { toTsv } from "@/components/tool-kit/charts/figure";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { BcgChart } from "@/components/tools/business-templates/bcg-chart";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type BcgMode,
  DEFAULT_GROWTH_LINE,
  DEFAULT_SHARE_LINE,
  layoutBcg,
  parseBcgRows,
  QUADRANTS,
  quadrantOf,
  summarizeQuadrants,
} from "@/lib/tool-kit/business-templates/bcg";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { wrapText } from "@/lib/tool-kit/charts/text";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const BCG_MATRIX_MAKER_STRINGS = {
  tryExample: "Load the example",
  clear: "Clear",
  modeLabel: "What do you know about each product's share?",
  modes: {
    relative: "Relative market share",
    shares: "Market shares (yours and the largest rival's)",
  },
  dataLabels: {
    relative:
      "Your products — one per line: name, revenue, market growth %, relative share",
    shares:
      "Your products — one per line: name, revenue, market growth %, your share %, largest rival's share %",
  },
  dataHint:
    "Separate with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers is read as a header. Growth may be negative; revenue may be left blank.",
  examples: {
    relative:
      "Product, Revenue, Growth %, Relative share\nOat Drinks, 150, 16, 1.8\nFrozen Meals, 420, 3, 2.4\nCanned Soup, 260, 1, 1.3\nProtein Snacks, 90, 14, 0.4\nMeal Kits, 60, 22, 0.15\nTinned Fish, 70, -2, 0.3\nPlain Biscuits, 110, 0, 0.6",
    shares:
      "Product, Revenue, Growth %, Your share %, Largest rival %\nOat Drinks, 150, 16, 27, 15\nFrozen Meals, 420, 3, 36, 15\nCanned Soup, 260, 1, 26, 20\nProtein Snacks, 90, 14, 8, 20\nMeal Kits, 60, 22, 3, 20\nTinned Fish, 70, -2, 6, 20\nPlain Biscuits, 110, 0, 12, 20",
  },
  linesHeading: "Dividing lines",
  shareLineLabel: "Relative share line",
  shareLineHint: "Convention: 1.0 — level with the largest competitor.",
  growthLineLabel: "Market growth line (% a year)",
  growthLineHint:
    "Convention: 10% a year. Use your market's own average if growth is slow.",
  chartHeading: "Labels",
  titleLabel: "Chart title",
  titlePlaceholder: "Growth-share matrix",
  unitLabel: "Revenue unit (optional)",
  unitPlaceholder: "e.g. £m",
  defaultTitle: "Growth-Share Matrix",
  plotted: "one:{count} product plotted|other:{count} products plotted",
  skipped: "one:{count} line was skipped.|other:{count} lines were skipped.",
  issueMissing: "Line {line} needs a name, a growth rate and a share.",
  issueShare: "Line {line}: the share must be above 0.",
  issueRevenue: "Line {line}: revenue cannot be negative.",
  badLine:
    "A dividing line needs a number (and a share line above 0), so the convention is used instead.",
  emptyPrompt: "Add a product above to see it on the matrix.",
  resultsHeading: "Where your products fall",
  quadrantNames: {
    star: "Stars",
    question: "Question marks",
    cow: "Cash cows",
    dog: "Dogs",
  },
  quadrantAction: {
    star: "Invest to keep the lead",
    question: "Invest selectively or divest",
    cow: "Maintain and harvest cash",
    dog: "Harvest or divest",
  },
  statLine: "{count} — {share} of revenue",
  tableHead: {
    product: "Product",
    quadrant: "Quadrant",
    share: "Relative share",
    growth: "Growth",
    revenue: "Revenue",
    revenueShare: "% of revenue",
    action: "Textbook response",
  },
  noRevenue: "—",
  tableHeading: "Product table",
  tableNote:
    "The responses are the usual textbook readings of each quadrant. They are prompts for a discussion, not instructions: the matrix says nothing about margins, strategy fit or how far the market can still grow.",
  xTitle: "Relative market share (log scale, leader on the left)",
  yTitle: "Market growth rate (% a year)",
  xTick: "{value}×",
  yTick: "{value}%",
  footerBase:
    "Bubble area is proportional to revenue. Dashed lines: relative share {share}×, growth {growth}%. Framework: B. D. Henderson, BCG, 1970.",
  footerUnit:
    "Bubble area is proportional to revenue ({unit}). Dashed lines: relative share {share}×, growth {growth}%. Framework: B. D. Henderson, BCG, 1970.",
  chartLabel: "Growth-share matrix",
  chartDescription:
    "A bubble chart. Relative market share runs along the bottom on a log scale with the largest share on the left, and market growth rate runs up the side. Dashed lines split it into stars, question marks, cash cows and dogs. Each bubble is a product; its area shows revenue.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyTable: "Copy table",
  copied: "Copied!",
  exportFailed: "The PNG could not be created. Download the SVG instead.",
  fileName: "bcg-matrix",
} as const;

/** Wrap width for the chart footer, a little inside the 760-unit drawing. */
const BCG_FOOTER_WIDTH = 700;

type Strings = typeof BCG_MATRIX_MAKER_STRINGS;

const MODE_OPTIONS = ["relative", "shares"] as const;

function lineValue(raw: string, fallback: number, positiveOnly: boolean) {
  const value = parseNumberInput(raw);
  if (raw.trim() === "") {
    return { value: fallback, bad: false };
  }
  const bad = value === null || (positiveOnly && value <= 0);
  return { value: bad || value === null ? fallback : value, bad };
}

function issueText(
  issue: { line: number; reason: "missing" | "share" | "revenue" },
  strings: Strings,
  fmt: (value: number) => string
) {
  const template = {
    missing: strings.issueMissing,
    share: strings.issueShare,
    revenue: strings.issueRevenue,
  }[issue.reason];
  return t(template, { line: fmt(issue.line) });
}

export default function BcgMatrixMakerWidget() {
  const strings = useToolUi(BCG_MATRIX_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<BcgMode>("relative");
  const [data, setData] = useState<string>(
    BCG_MATRIX_MAKER_STRINGS.examples.relative
  );
  const [shareText, setShareText] = useState("");
  const [growthText, setGrowthText] = useState("");
  const [title, setTitle] = useState("");
  const [unit, setUnit] = useState("");
  const [exportFailed, setExportFailed] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  const shareLine = lineValue(shareText, DEFAULT_SHARE_LINE, true);
  const growthLine = lineValue(growthText, DEFAULT_GROWTH_LINE, false);
  const { rows, issues } = useMemo(
    () => parseBcgRows(data, mode),
    [data, mode]
  );
  const layout = useMemo(
    () => layoutBcg(rows, shareLine.value, growthLine.value),
    [rows, shareLine.value, growthLine.value]
  );
  const summary = summarizeQuadrants(rows, shareLine.value, growthLine.value);
  const totalRevenue = rows.reduce((sum, row) => sum + (row.revenue ?? 0), 0);
  const chartTitle = title.trim() || strings.defaultTitle;
  const decimals = { maximumFractionDigits: 2 };
  const footer = wrapText(
    t(unit.trim() ? strings.footerUnit : strings.footerBase, {
      unit: unit.trim(),
      share: fmt(shareLine.value, decimals),
      growth: fmt(growthLine.value, decimals),
    }),
    BCG_FOOTER_WIDTH,
    { fontSize: 11 },
    2
  );
  const tableRows = rows.map((row) => {
    const quadrant = quadrantOf(row, shareLine.value, growthLine.value);
    return [
      row.name,
      strings.quadrantNames[quadrant],
      fmt(row.relativeShare, decimals),
      `${fmt(row.growth, decimals)}%`,
      row.revenue === null ? strings.noRevenue : fmt(row.revenue, decimals),
      row.revenue === null || totalRevenue === 0
        ? strings.noRevenue
        : fmt(row.revenue / totalRevenue, {
            style: "percent",
            maximumFractionDigits: 1,
          }),
      strings.quadrantAction[quadrant],
    ];
  });
  const tableHead = Object.values(strings.tableHead);
  const baseName = chartFilename(chartTitle, strings.fileName);

  function svg() {
    return holder.current?.querySelector("svg") ?? null;
  }

  async function savePng() {
    const element = svg();
    if (!element) {
      return;
    }
    try {
      setExportFailed(false);
      await downloadPng(`${baseName}.png`, element, { scale: 2 });
    } catch {
      setExportFailed(true);
    }
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4 print:hidden">
        <PillGroup<BcgMode>
          label={strings.modeLabel}
          name="bcg-mode"
          onChange={(next) => {
            setMode(next);
            const wasExample = data === strings.examples[mode];
            if (wasExample) {
              setData(strings.examples[next]);
            }
          }}
          options={MODE_OPTIONS.map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        <TextAreaField
          hint={strings.dataHint}
          id="bcg-data"
          label={strings.dataLabels[mode]}
          onChange={setData}
          rows={8}
          value={data}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setData(strings.examples[mode])}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setData("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title={strings.linesHeading}>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                hint={strings.shareLineHint}
                id="bcg-share-line"
                label={strings.shareLineLabel}
                numeric
                onChange={setShareText}
                placeholder={fmt(DEFAULT_SHARE_LINE)}
                value={shareText}
              />
              <TextField
                hint={strings.growthLineHint}
                id="bcg-growth-line"
                label={strings.growthLineLabel}
                numeric
                onChange={setGrowthText}
                placeholder={fmt(DEFAULT_GROWTH_LINE)}
                value={growthText}
              />
            </div>
            {shareLine.bad || growthLine.bad ? (
              <ErrorNotice>{strings.badLine}</ErrorNotice>
            ) : null}
          </Panel>
          <Panel title={strings.chartHeading}>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="bcg-title"
                label={strings.titleLabel}
                onChange={setTitle}
                placeholder={strings.titlePlaceholder}
                value={title}
              />
              <TextField
                id="bcg-unit"
                label={strings.unitLabel}
                onChange={setUnit}
                placeholder={strings.unitPlaceholder}
                value={unit}
              />
            </div>
          </Panel>
        </div>
      </div>

      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {rows.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <p className={HINT}>
            {tPlural(strings.plotted, locale, rows.length, {
              count: fmt(rows.length),
            })}
          </p>
        )}
        {issues.length > 0 ? (
          <ErrorNotice>
            {issues.map((issue) => issueText(issue, strings, fmt)).join(" ")}
          </ErrorNotice>
        ) : null}
        <StatGrid>
          {QUADRANTS.map((quadrant) => {
            const entry = summary.find((item) => item.quadrant === quadrant);
            return (
              <StatCard
                key={quadrant}
                label={strings.quadrantNames[quadrant]}
                value={t(strings.statLine, {
                  count: fmt(entry?.count ?? 0),
                  share: fmt(entry?.revenueShare ?? 0, {
                    style: "percent",
                    maximumFractionDigits: 0,
                  }),
                })}
              />
            );
          })}
        </StatGrid>
      </section>

      <figure className="space-y-3">
        <div
          className="overflow-hidden rounded-lg border border-border bg-white"
          ref={holder}
        >
          <BcgChart
            id="bcg-chart"
            layout={layout}
            text={{
              title: chartTitle,
              xTitle: strings.xTitle,
              yTitle: strings.yTitle,
              quadrants: strings.quadrantNames,
              xTick: (value) => t(strings.xTick, { value: fmt(value) }),
              yTick: (value) => t(strings.yTick, { value: fmt(value) }),
              footer,
              label: strings.chartLabel,
              description: strings.chartDescription,
            }}
          />
        </div>
        <div className="flex flex-wrap gap-3 print:hidden">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              const element = svg();
              if (element) {
                downloadSvg(`${baseName}.svg`, element);
              }
            }}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button className={BUTTON_SECONDARY} onClick={savePng} type="button">
            {strings.downloadPng}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyTable}
            text={toTsv([tableHead, ...tableRows])}
          />
        </div>
        {exportFailed ? (
          <ErrorNotice>{strings.exportFailed}</ErrorNotice>
        ) : null}
      </figure>

      {rows.length > 0 ? (
        <section className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
          <DataTable head={tableHead} rows={tableRows} />
          <p className={HINT}>{strings.tableNote}</p>
        </section>
      ) : null}
    </div>
  );
}
